import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFirebase } from "@/contexts/FirebaseContext";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, MessageSquare, Clock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getEnrollmentsByUserId, Enrollment } from "@/utils/firestoreService";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Progress } from "@/components/ui/progress";
import ScheduleClassDialog from "@/components/scheduling/ScheduleClassDialog";

const BookingsList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment | null>(null);
  const [isSchedulingOpen, setIsSchedulingOpen] = useState(false);

  useEffect(() => {
    const fetchEnrollments = async () => {
      if (!user?.id) {
        console.log("No current user found");
        toast({
          title: "Authentication Error",
          description: "Please login to view your bookings",
          variant: "destructive",
        });
        return;
      }

      setLoading(true);
      try {
        const fetchedEnrollments = await getEnrollmentsByUserId(user.id);
        setEnrollments(fetchedEnrollments);
      } catch (error) {
        console.error("Error fetching enrollments:", error);
        toast({
          title: "Error",
          description: "Failed to load your bookings. Please try again.",
          variant: "destructive",
        });
        setEnrollments([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      fetchEnrollments();
    }
  }, [user, toast]);

  const handleQuickBook = () => {
    // Find the most recent practical course enrollment
    const practicalEnrollments = enrollments.filter(
      enrollment => enrollment.courseType === "practical"
    ).sort((a, b) => {
      const dateA = a.enrolledAt ? new Date(a.enrolledAt).getTime() : 0;
      const dateB = b.enrolledAt ? new Date(b.enrolledAt).getTime() : 0;
      return dateB - dateA;
    });

    if (practicalEnrollments.length > 0) {
      setSelectedEnrollment(practicalEnrollments[0]);
      setIsSchedulingOpen(true);
    } else {
      toast({
        title: "No Practical Course Found",
        description: "Please enroll in a practical course first.",
        variant: "destructive",
      });
    }
  };

  return (
    <DashboardLayout role="student">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">My Bookings</h1>
          <Button onClick={handleQuickBook}>
            Book a Lesson
          </Button>
        </div>

        <Tabs defaultValue="upcoming" className="space-y-4">
          <TabsList>
            <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="past">Past</TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="space-y-4">
            {loading ? (
              <div>Loading...</div>
            ) : enrollments.length === 0 ? (
              <div>No upcoming bookings found.</div>
            ) : (
              enrollments.map((enrollment) => (
                <Card key={enrollment.id}>
                  <CardHeader>
                    <CardTitle>{enrollment.courseName}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4" />
                          <span>Enrolled: {format(new Date(enrollment.enrolledAt || ""), "MMM dd, yyyy")}</span>
                        </div>
                        <Badge>{enrollment.status}</Badge>
                      </div>
                      {enrollment.courseType === "practical" && (
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Progress</span>
                            <span>{enrollment.hoursTaken || 0} / {enrollment.totalHours || 0} hours</span>
                          </div>
                          <Progress value={(enrollment.hoursTaken || 0) / (enrollment.totalHours || 1) * 100} />
                        </div>
                      )}
                      <div className="flex justify-end space-x-2">
                        <Button variant="outline" onClick={() => navigate(`/student-dashboard/messages/${enrollment.instructorId}`)}>
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Message Instructor
                        </Button>
                        {enrollment.courseType === "practical" && (
                          <Button onClick={() => {
                            setSelectedEnrollment(enrollment);
                            setIsSchedulingOpen(true);
                          }}>
                            <Clock className="h-4 w-4 mr-2" />
                            Schedule Class
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="past" className="space-y-4">
            {/* Past bookings content */}
          </TabsContent>
        </Tabs>

        {selectedEnrollment && (
          <ScheduleClassDialog
            open={isSchedulingOpen}
            onOpenChange={setIsSchedulingOpen}
            enrollmentId={selectedEnrollment.id}
            courseId={selectedEnrollment.courseId}
            hoursRemaining={selectedEnrollment.hoursRemaining || 0}
            onScheduled={() => {
              setIsSchedulingOpen(false);
              // Refresh enrollments
              if (user?.id) {
                getEnrollmentsByUserId(user.id).then(setEnrollments);
              }
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default BookingsList;
